import { createMuiTheme, darken } from "@material-ui/core/styles";
import React, { createContext } from "react";
import useLocalStorage from "react-use/lib/useLocalStorage";
import useMedia from "react-use/lib/useMedia";
const colors = {
    light: {
        default: {
            light: "#dd33fa",
            main: "#d500f9",
            dark: "#9500ae",
            contrastText: "#fff"
        },
        primary: {
            light: "#FEC8D7",
            main: "#FA255E",
            dark: "#DB2052",
            contrastText: "#fff"
        },
        secondary: {
            light: "#E3E4E5",
            main: "#1f2833",
            dark: "#0F141A",
            contrastText: "#fff"
        },
        background: {
            default: "#FFFFFF",
            paper: "#f4f4f4"
        },
        text: {
            primary: "#1f2833",
            secondary: "#ffffff"
        }
    },
    dark: {
        default: {
            light: "#dd33fa",
            main: "#d500f9",
            dark: "#9500ae",
            contrastText: "#fff"
        },
        primary: {
            light: "#FEC8D7",
            main: "#FA255E",
            dark: "#DB2052",
            contrastText: "#fff"
        },
        secondary: {
            light: "#E8E8E9",
            main: "#a1a1a1",
            dark: "#7F7F7F",
            contrastText: "#FA255E"
        },
        background: {
            default: "#202020",
            paper: "#2b2b2b"
        },
        text: {
            primary: "#ffffff",
            secondary: "#ffffff"
        }
    }
};
const createLabOverrides = (type) => ({
    MuiAlert: {
        icon: {
            fontSize: 24
        },
        message: {
            transform: "translateY(-1px)"
        }
    },
    MuiAlertTitle: {
        root: {
            color: type === "dark" ? "white" : null,
            marginTop: -3,
            fontWeight: 600
        }
    }
});
export const createKvixTheme = (type) => createMuiTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1104,
            xl: 1400
        }
    },
    typography: {
        body1: {
            lineHeight: 1.7
        },
        body2: {
            fontSize: 14,
            letterSpacing: "0.25px",
            lineHeight: "20px"
        },
        h1: {
            fontSize: 56,
            lineHeight: "64px",
            fontWeight: "bold",
            letterSpacing: -1
        },
        h2: {
            fontSize: 36,
            fontWeight: "bold",
            lineHeight: "44px",
            "@media (max-width:600px)": {
                fontSize: 28,
                lineHeight: "36px"
            }
        },
        h3: {
            fontSize: 28,
            fontWeight: "bold",
            lineHeight: "32px",
            "@media (max-width:600px)": {
                fontSize: 22,
                lineHeight: "28px"
            }
        },
        h4: {
            fontSize: 18,
            fontWeight: "bold",
            letterSpacing: "0.18px",
            lineHeight: "24px",
            "@media (max-width:600px)": {
                fontSize: 18,
                lineHeight: "24px"
            }
        },
        h5: {
            fontSize: 14,
            fontWeight: "bold",
            letterSpacing: "0.25px",
            lineHeight: "20px"
        },
        h6: {
            fontSize: 14,
            fontWeight: "bold",
            letterSpacing: "0.11px",
            lineHeight: "24px"
        },
        caption: {
            fontSize: 10,
            fontWeight: "bold",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            lineHeight: 2.25
        },
        button: {
            fontSize: 14,
            fontWeight: "bold",
            letterSpacing: "0.5px",
            textTransform: "none"
        },
        fontFamily: [
            "Twemoji Country Flags",
            "Avenir Next",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            '"Helvetica Neue"',
            "Arial",
            "sans-serif",
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"'
        ].join(",")
    },
    palette: {
        type: type,
        background: {
            default: colors[type].background.default,
            paper: colors[type].background.paper
        },
        text: {
            primary: colors[type].text.primary,
            secondary: colors[type].text.secondary
        },
        primary: {
            main: colors[type].primary.main,
            dark: colors[type].primary.dark
        },
        secondary: {
            main: colors[type].secondary.main,
            dark: colors[type].secondary.dark
        },
        success: {
            main: type === "dark" ? "#52aa55" : "#258e28"
        }
    },
    props: {
        MuiCheckbox: {
            color: "primary"
        },
        MuiRadio: {
            color: "primary"
        },
        MuiButton: {
            disableElevation: true
        }
    },
    overrides: Object.assign({ MuiTypography: {
            h1: {
                "&$gutterBottom": {
                    marginBottom: 20
                }
            },
            h4: {
                "&$gutterBottom": {
                    marginBottom: 12
                }
            },
            body1: {
                color: type === "dark" ? darken(colors[type].text.primary, 0.2) : null
            },
            body2: {
                color: type === "dark" ? darken(colors[type].text.primary, 0.2) : null
            }
        }, MuiCheckbox: {
            root: {
                "& + .MuiFormControlLabel-label": {
                    transform: "translateY(-0.06em)"
                }
            }
        }, MuiRadio: {
            root: {
                padding: "6px 8px"
            }
        }, MuiContainer: {
            root: {
                paddingLeft: 32,
                paddingRight: 32
            }
        }, MuiPaper: {
            elevation1: {
                boxShadow: "none"
            },
            rounded: {
                borderRadius: 0
            }
        }, MuiChip: {
            root: {
                height: 32,
                borderRadius: 6,
                backgroundColor: colors[type].background.paper
            },
            label: {
                fontSize: 14,
                letterSpacing: 0.25,
                transform: "translateY(-1px)"
            }
        }, MuiBadge: {
            badge: {
                margin: 4
            }
        }, MuiAppBar: {
            colorDefault: {
                backgroundColor: "#FFFFFF"
            }
        }, MuiDivider: {
            light: {
                height: 2
            }
        }, MuiListItem: {
            root: {
                height: 40
            },
            button: {
                "& + &": {
                    borderTop: "1px solid rgba(0, 0, 0, 0.075)"
                }
            }
        }, MuiListItemText: {
            root: {
                "& > span": {
                    transform: "translateY(-0.12em)",
                    display: "inline-block"
                }
            },
            primary: {
                color: type === "dark" ? null : "#1F2833",
                fontSize: 14,
                fontWeight: "bold",
                letterSpacing: ".5px"
            }
        }, MuiMenu: {
            paper: {
                borderRadius: 3,
                boxShadow: "0px 0px 40px rgba(0, 0, 0, 0.1), 0px 15px 22px rgba(0, 0, 0, 0.1)"
            },
            list: {
                paddingTop: 0,
                paddingBottom: 0
            }
        }, MuiMenuItem: {
            root: {
                fontSize: 14,
                fontWeight: 600
            }
        }, MuiInputBase: {
            root: {
                "&.MuiAutocomplete-inputRoot": {
                    padding: "14.5px 14px !important",
                    "& > .MuiAutocomplete-input": {
                        padding: "0 !important"
                    }
                }
            }
        }, MuiSelect: {
            select: {
                "&:focus": {
                    backgroundColor: "transparent"
                }
            },
            filled: {
                borderRadius: 6,
                padding: "10px 12px 12px 16px",
                fontSize: 15,
                fontWeight: 600,
                transition: "filter 0.2s ease",
                "&:focus": {
                    borderRadius: 6,
                    filter: "brightness(0.9) saturate(0.7)"
                }
            },
            iconFilled: {
                color: "inherit"
            }
        }, MuiInput: {
            root: {
                height: 44,
                backgroundColor: type === "dark" ? "#2b2b2b" : "#f1f1f1",
                borderRadius: 6,
                "&.Mui-focused": {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0
                }
            },
            underline: {
                backgroundColor: type === "dark" ? "#2b2b2b" : "#f1f1f1",
                "&:hover:not($disabled):before": {
                    borderBottomColor: "#FA255E",
                    borderBottomWidth: 1
                },
                "&:before": {
                    borderBottomColor: "transparent"
                },
                "&:after": {
                    borderBottomColor: "#FA255E"
                }
            }
        }, MuiInputLabel: {
            root: {
                color: type === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.6)",
                fontSize: 14,
                fontWeight: 500
            },
            shrink: {
                color: type === "dark" ? "rgba(255, 255, 255, 0.65)" : "rgba(0, 0, 0, 0.6)",
                "&$error": {
                    fontWeight: 500
                },
                "&$focused": {
                    fontWeight: 600,
                    marginTop: "0.05em"
                }
            },
            outlined: {
                transform: "translate(14px, 16px) scale(1)",
                "&$shrink": {
                    transform: "translate(14px, -6px) scale(0.85)"
                }
            }
        }, MuiLink: {
            root: {
                fontWeight: 500,
                letterSpacing: "0.25px",
                lineHeight: "20px"
            }
        }, MuiOutlinedInput: {
            root: {
                "&$error": {
                    backgroundColor: "rgba(250, 37, 94, 0.04)"
                },
                "&$disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.03)",
                    opacity: 0.75
                }
            },
            input: {
                padding: "14.5px 14px",
                fontSize: 14
            },
            notchedOutline: {
                borderRadius: 3,
                borderColor: type === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.3)"
            }
        }, MuiFormLabel: {
            root: {
                color: type === "dark" ? null : "rgba(0, 0, 0, 0.7)",
                fontSize: 14,
                "& + [role='radiogroup']": {
                    marginTop: 6
                }
            }
        }, MuiFormHelperText: {
            root: {
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: "normal",
                textTransform: "none"
            }
        }, MuiDialog: {
            paper: {
                backgroundColor: type === "dark" ? "" : "white"
            }
        }, MuiDialogActions: {
            root: {
                padding: "16px 24px"
            }
        }, MuiFormControlLabel: {
            root: {
                "&$labelPlacementStart": {
                    marginLeft: 0,
                    width: "100%",
                    justifyContent: "space-between"
                }
            },
            label: {
                userSelect: "none",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.25px",
                lineHeight: "20px"
            }
        }, MuiButtonGroup: {
            grouped: {
                "& .MuiButton-label": {
                    fontSize: 13
                }
            }
        }, MuiButton: {
            root: {
                borderRadius: 26,
                height: 52,
                padding: "0 25px",
                boxShadow: "none",
                whiteSpace: "nowrap"
            },
            outlinedPrimary: {
                "&.MuiButtonGroup-groupedOutlined": {
                    "& $label": {
                        fontWeight: 600,
                        transform: "translateY(0.02em)"
                    }
                }
            },
            fullWidth: {
                padding: "0 20px"
            },
            label: {
                transform: "translateY(-0.06em)"
            },
            contained: {
                color: colors[type].primary,
                boxShadow: "none"
            },
            text: {
                fontWeight: 700,
                lineHeight: 1,
                "& $label > svg:first-child": {
                    marginRight: 8
                }
            },
            textSizeLarge: {
                fontSize: 24,
                fontWeight: "bold",
                letterSpacing: "0.18px",
                "& $label": {
                    height: 32
                }
            },
            sizeSmall: {
                height: 36,
                padding: "4px 12px 3px 12px",
                "& $label": {
                    fontSize: 14
                }
            }
        }, MuiSnackbar: {
            anchorOriginTopRight: {
                marginTop: 60
            }
        }, MuiSnackbarContent: {
            root: {
                backgroundColor: "#FA255E"
            },
            message: {
                padding: 4,
                fontSize: 14,
                fontWeight: 600,
                display: "flex",
                alignItems: "center"
            }
        }, MuiTable: {
            root: {
                backgroundColor: type == "dark" ? "#2b2b2b" : "white",
                borderRadius: 6
            }
        }, MuiTableCell: {
            head: {
                fontWeight: 600
            },
            body: {
                padding: "14px 16px",
                "&.border-none": {
                    borderBottom: type == "dark" ? "none" : "1px solid rgba(224, 224, 224, 1)"
                }
            }
        }, MuiStepIcon: {
            text: {
                fontWeight: 600
            }
        }, MuiStepLabel: {
            label: {
                "&$alternativeLabel": {
                    marginTop: 10
                }
            }
        }, MuiTab: {
            root: {
                fontWeight: 600,
                "@media (min-width: 600px)": {
                    fontSize: 12,
                    minWidth: 140
                }
            },
            textColorPrimary: {
                color: "rgba(0, 0, 0, 0.8)"
            }
        }, MuiTabs: {
            scroller: {
                display: "inline-flex"
            }
        } }, createLabOverrides(type))
});
export const KvixThemeVariantContext = createContext(null);
export const KvixThemeProvider = (props) => {
    const prefersDarkMode = useMedia("(prefers-color-scheme: dark)", false);
    const [enabledState, setEnabledState] = useLocalStorage("dark-mode-enabled");
    const isAuto = typeof enabledState === "undefined";
    const isDarkMode = isAuto ? prefersDarkMode : enabledState;
    const variant = props.allowDarkMode ? (isDarkMode ? "dark" : "light") : "light";
    const theme = createKvixTheme(variant);
    const setVariant = (value) => {
        if (value === "auto")
            return;
        setEnabledState(value === "dark");
    };
    return (React.createElement(KvixThemeVariantContext.Provider, { value: {
            setVariant,
            variant
        } }, props.children(theme)));
};
//# sourceMappingURL=theme.js.map